<template>
  <div class="flex flex-col gap-6" data-cy="job-cost-tab">
    <p v-if="loading" class="text-gray-600" data-cy="job-cost-loading">
      Loading job cost...
    </p>
    <p v-else-if="loadFailed" class="text-red-600" data-cy="job-cost-error">
      Job cost couldn't be loaded. Refresh the page to try again.
    </p>
    <p v-else-if="!jobCost" class="text-gray-600" data-cy="job-cost-empty">
      Job cost isn't available for this job.
    </p>
    <template v-else>
      <section>
        <div class="flex flex-wrap items-center gap-3 mt-4">
          <h3 class="page-header-2">Job Profit / Margin</h3>
          <Badge
            v-if="stateMarker"
            variant="chip"
            :color="stateMarker.color"
            data-cy="job-cost-state"
            :text="stateMarker.label"
            :tooltip-text="stateMarker.tooltip"
          />
          <Badge
            v-if="jobCost.estimated"
            variant="chip"
            :color="ESTIMATED_MARKER.color"
            data-cy="job-cost-estimated"
            :text="ESTIMATED_MARKER.label"
            :tooltip-text="estimateTooltip(jobCost.estimate_reasons)"
          />
        </div>
        <dl
          class="mt-2 rounded-md border border-gray-200 bg-white divide-y divide-gray-200"
          data-cy="job-cost-ledger"
        >
          <div
            v-for="row in ledgerRows"
            :key="row.key"
            class="flex items-start justify-between gap-4 px-4 py-3"
            :class="{ 'border-t-2 border-t-gray-300': row.divider }"
            :data-cy="`job-cost-row-${row.key}`"
          >
            <dt :class="row.strong ? 'font-semibold' : 'font-normal'">
              <div>{{ row.label }}</div>
              <div v-if="row.subtext" class="text-xs font-normal text-gray-600">
                {{ row.subtext }}
              </div>
            </dt>
            <dd
              class="text-right"
              :class="[row.valueClass, row.strong ? 'font-semibold' : '']"
              data-cy="job-cost-row-value"
            >
              <JobCostValue
                :value="row.value"
                :format="row.format"
                :cost="row.cost"
                :not-available="timesInvalid"
              />
            </dd>
          </div>
        </dl>
      </section>

      <JobCostPricebookTime
        :job-cost="jobCost"
        :worked-minutes="legTotals.total_minutes"
        :people-label="peopleLabel"
        :times-invalid="timesInvalid"
        :department-id="store.state.job?.department?.id"
      />

      <!-- The table has no pager or info, so its bottom layout row is empty. -->
      <section
        class="rounded-md border border-gray-200 bg-white [&_.dt-container>.row:last-child]:hidden"
        data-cy="job-cost-labor"
      >
        <div class="border-b border-gray-200 bg-gray-50 px-4 py-3 text-sm">
          <b>Labor</b>
          <span class="text-gray-600">
            per person — pre-work, travel and work, like the Job Times report
          </span>
        </div>
        <DatatableV3
          v-if="laborRows.length"
          id="job-cost-labor"
          :key="tableKey"
          :config="laborConfig"
          :rows="laborRows"
          hide-header
          flat
          no-download
          hide-filters
          hide-page-length-select
          :auto-height="false"
          :min-height="0"
          data-cy="job-cost-labor-table"
        >
          <template #column-person="row">
            <div class="flex items-center gap-2" data-cy="job-cost-person">
              {{ row.person }}
              <Badge
                v-if="row.is_total && hasReason('department_rate_fallback')"
                variant="chip"
                :color="ESTIMATED_MARKER.color"
                :text="ESTIMATED_MARKER.label"
                :tooltip-text="estimateTooltip(['department_rate_fallback'])"
              />
            </div>
          </template>
          <template
            v-for="column in legColumns"
            :key="column.key"
            #[`column-${column.key}`]="row"
          >
            <MinutesLabel
              :minutes="row.columnValue"
              :not-available="timesInvalid"
              :not-available-text="NOT_AVAILABLE_JOB_TIME_TOOLTIP_TEXT"
            />
          </template>
          <template #column-rate_cents="row">
            <div
              v-if="!row.is_total"
              class="flex items-center justify-end gap-2"
              data-cy="job-cost-rate"
            >
              <Badge
                v-if="row.rate_source === 'department_fallback'"
                variant="chip"
                color="yellow"
                data-cy="job-cost-rate-fallback"
                text="DEPT"
                :tooltip-text="FALLBACK_RATE_TOOLTIP"
              />
              <AppLink
                :to="row.profile_path"
                :text="dollars(row.rate_cents)"
                data-cy="job-cost-rate-link"
              />
              <Badge
                v-if="row.rate_varies"
                variant="chip"
                color="yellow"
                data-cy="job-cost-rate-varies"
                text="Rate changed"
                :tooltip-text="RATE_VARIES_TOOLTIP"
              />
            </div>
          </template>
          <template #column-labor_cost_cents="row">
            <span data-cy="job-cost-line-cost">
              <JobCostValue :value="row.labor_cost_cents" />
            </span>
          </template>
        </DatatableV3>
        <p v-else class="px-4 py-3 text-gray-600" data-cy="job-cost-no-labor">
          <JobCostValue v-if="timesInvalid" :value="null" not-available />
          <template v-else>No labor recorded</template>
        </p>
      </section>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";

import Badge from "@/components/Badge.vue";
import DatatableV3 from "@/components/DatatableV3.vue";
import AppLink from "@/components/Link.vue";
import MinutesLabel from "@/components/MinutesLabel.vue";
import { useBreakpoints } from "@/composables/useBreakpoints";
import CONSTANTS from "@/Constants.js";
import {
  ESTIMATED_MARKER,
  estimateTooltip,
  FALLBACK_RATE_TOOLTIP,
  RATE_VARIES_TOOLTIP,
  STATE_MARKERS
} from "@/Job/jobCostMarkers";
import { dollars, formatDuration, pluralize } from "@/mixins";
import type {
  JobCost,
  JobCostEstimateReason,
  JobCostMinutes
} from "@/typings/jobCost";
import JobCostPricebookTime from "./JobCostPricebookTime.vue";
import JobCostValue from "./JobCostValue.vue";

const { NOT_AVAILABLE_JOB_TIME_TOOLTIP_TEXT } = CONSTANTS;
// Responsive priority: lower stays visible longer on a phone. Money outranks
// the time legs, which fold into the tap-to-expand row first.
const LEG_COLUMNS: {
  key: keyof JobCostMinutes;
  label: string;
  priority: number;
}[] = [
  { key: "pre_work_minutes", label: "Pre-Work", priority: 7 },
  { key: "travel_minutes", label: "Travel", priority: 6 },
  { key: "work_minutes", label: "Work", priority: 5 },
  { key: "total_minutes", label: "Total", priority: 4 }
];

const store = useStore();
// Responsive can't be switched on a live table, so crossing md remounts it.
const { type: layoutType } = useBreakpoints();
// DataTables reads its columns once, so a change to them needs a fresh table too.
const tableKey = computed(
  () =>
    `${layoutType.value}-${jobCost.value?.rates_visible}-${legColumns.value.length}`
);

const jobCost = ref<JobCost | null>(null);
const loading = ref(true);
const loadFailed = ref(false);

const timesInvalid = computed(() => jobCost.value?.state === "times_invalid");
const stateMarker = computed(() =>
  jobCost.value ? STATE_MARKERS[jobCost.value.state] : undefined
);
const legColumns = computed(() =>
  store.getters.settingEnabledForTenant("pre_work_enabled")
    ? LEG_COLUMNS
    : LEG_COLUMNS.filter((column) => column.key !== "pre_work_minutes")
);
const ledgerRows = computed(() => {
  const cost = jobCost.value as JobCost;
  return [
    { key: "revenue", label: "Revenue", value: cost.revenue_cents },
    {
      key: "labor",
      label: "Labor",
      subtext: timesInvalid.value
        ? ""
        : `${peopleLabel.value} · ${formatDuration(legTotals.value.total_minutes)} worked`,
      value: cost.labor_cost_cents,
      cost: true
    },
    {
      key: "parts",
      label: "Parts",
      subtext: "pricebook parts cost",
      value: cost.parts_cost_cents,
      cost: true
    },
    {
      key: "finance_fee",
      label: "Finance fee",
      subtext: "Finance Markup on parts",
      value: cost.finance_fee_cents,
      cost: true
    },
    // Most companies don't use purchase orders, so the line shows only on a job that has some.
    ...(cost.purchase_orders_cost_cents === 0
      ? []
      : [
          {
            key: "purchase_orders",
            label: "Purchase orders",
            subtext: "non-voided purchase orders on this job",
            value: cost.purchase_orders_cost_cents,
            cost: true
          }
        ]),
    {
      key: "cost",
      divider: true,
      label: "COGS",
      value: cost.job_cost_cents,
      cost: true,
      strong: true
    },
    {
      key: "profit",
      label: "Job profit",
      value: cost.job_profit_cents,
      strong: true,
      valueClass: `text-xl ${signClass(cost.job_profit_cents, "text-green-700")}`
    },
    {
      key: "margin",
      label: "Margin",
      value: cost.margin_pct,
      format: "percent" as const,
      strong: true,
      valueClass: `text-xl ${signClass(cost.margin_pct, "text-blue-800")}`
    }
  ];
});

const peopleLabel = computed(() => {
  const count = jobCost.value?.labor_lines.length ?? 0;
  return `${count} ${pluralize(count, "person", "people")}`;
});

// Minutes are display aggregates; every cost total comes from the server.
const legTotals = computed(() => {
  const lines = jobCost.value?.labor_lines ?? [];
  return Object.fromEntries(
    LEG_COLUMNS.map(({ key }) => [
      key,
      lines.reduce((total, line) => total + line[key], 0)
    ])
  ) as Record<keyof JobCostMinutes, number>;
});

// The person rows plus a closing total; ordering is off, so it stays last.
const laborRows = computed(() => {
  const cost = jobCost.value;
  if (!cost?.labor_lines.length) return [];
  const lines = cost.labor_lines.map((line) => ({
    ...line,
    id: `${line.person.type}-${line.person.id}`,
    person: line.person.name,
    profile_path: line.person.profile_path,
    is_total: false
  }));
  return [
    ...lines,
    {
      ...legTotals.value,
      id: "total",
      person: "Total labor",
      rate_cents: null,
      labor_cost_cents: cost.labor_cost_cents,
      is_total: true
    }
  ];
});

const laborConfig = computed(() => {
  const right = "text-right";
  const columns = [
    {
      data: "person",
      title: "Technician",
      className: "wrap-when-responsive",
      responsivePriority: 1
    },
    ...legColumns.value.map((column) => ({
      data: column.key,
      title: column.label,
      className: right,
      responsivePriority: column.priority
    })),
    // The payload's flag, not currentAdmin's: it comes from the same request
    // as rate_cents, so the two can't disagree.
    ...(jobCost.value?.rates_visible
      ? [
          {
            data: "rate_cents",
            title: "Rate / Hr",
            className: right,
            responsivePriority: 3
          }
        ]
      : []),
    {
      data: "labor_cost_cents",
      title: "Labor Cost",
      className: right,
      responsivePriority: 2
    }
  ];
  return {
    // Off per column too: the header sort classes read col.orderable, not
    // ordering: false.
    columns: columns.map((column) => ({ ...column, orderable: false })),
    responsive: true,
    paging: false,
    info: false,
    ordering: false,
    searching: false,
    createdRow: (row: HTMLTableRowElement, data: { is_total: boolean }) => {
      row.dataset.cy = data.is_total
        ? "job-cost-labor-total"
        : "job-cost-labor-row";
      if (data.is_total) row.classList.add("font-semibold");
    }
  };
});

function signClass(value: number | null, positiveClass: string): string {
  if (value === null || value === 0) return "";
  return value < 0 ? "text-red-600" : positiveClass;
}

function hasReason(reason: JobCostEstimateReason): boolean {
  return jobCost.value?.estimate_reasons.includes(reason) ?? false;
}

async function loadJobCost() {
  try {
    const fetched = await store.dispatch("fetchJobCost", store.state.job.id);
    // Most job refreshes leave the costs as they were; skip the table redraw.
    if (JSON.stringify(fetched) !== JSON.stringify(jobCost.value)) {
      jobCost.value = fetched;
    }
    loadFailed.value = false;
  } catch {
    loadFailed.value = true;
  } finally {
    loading.value = false;
  }
}

onMounted(loadJobCost);
// JobPage replaces the job on refresh-job and appointment updates; the costs
// derive from the same records, so reload them in place (no loading flash).
watch(() => store.state.job, loadJobCost);
</script>
